/**
 * Queries and mutations for the Migrate area (`/migrate/*`), migrator-only.
 *
 * Its own key root. The lookup is a mutation even though it writes nothing:
 * it is an explicit "go" on a number the reviewer typed, and it can take a
 * while (the classifier runs), so it should not refire on focus or remount.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type AssistantRequestFormEvent,
  getMigrateRequestForm as sdkGetMigrateRequestForm,
  getMigrateStatus as sdkGetMigrateStatus,
  importLegacyTicket as sdkImportLegacyTicket,
  type LegacySummary,
  listMigrationHistory as sdkListMigrationHistory,
  lookupLegacyTicket as sdkLookupLegacyTicket,
  type MigrateImportRequest,
  type MigrateImportResponse,
  type MigrateStatus,
  type MigrationHistoryResponse,
} from "@/lib/api";
import { logger } from "@/lib/logger";

export const migrateKeys = {
  all: ["migrate"] as const,
  status: () => [...migrateKeys.all, "status"] as const,
  history: () => [...migrateKeys.all, "history"] as const,
  requestForm: (legacyNumber: string, offeringId: string, customerEmail: string | null) =>
    [...migrateKeys.all, "request-form", legacyNumber, offeringId, customerEmail] as const,
};

/** Whether the legacy CRM can be read as the signed-in migrator. */
export function useMigrateStatus() {
  return useQuery({
    queryKey: migrateKeys.status(),
    queryFn: async (): Promise<MigrateStatus> => {
      const { data } = await sdkGetMigrateStatus({ throwOnError: true });
      return data;
    },
    retry: false,
  });
}

/** Read one legacy ticket and its suggested classification. Writes nothing. */
export function useLegacyLookup() {
  return useMutation({
    mutationFn: async (legacyNumber: string): Promise<LegacySummary> => {
      logger.debug("[migrate] lookup", legacyNumber);
      const { data } = await sdkLookupLegacyTicket({
        body: { legacy_number: legacyNumber },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
  });
}

/** The offering's form, prefilled from the legacy ticket and the customer. */
export function useMigrateRequestForm(
  legacyNumber: string,
  offeringId: string | null,
  customerEmail: string | null,
) {
  return useQuery({
    queryKey: migrateKeys.requestForm(legacyNumber, offeringId ?? "", customerEmail),
    queryFn: async (): Promise<AssistantRequestFormEvent> => {
      const { data } = await sdkGetMigrateRequestForm({
        body: {
          legacy_number: legacyNumber,
          offering_id: offeringId ?? "",
          customer_email: customerEmail,
        },
        throwOnError: true,
      });
      return data;
    },
    enabled: offeringId !== null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export interface ImportVariables {
  body: MigrateImportRequest;
  idempotencyKey: string;
}

/** Import the legacy ticket. Idempotent server-side on the legacy number. */
export function useImportLegacyTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      body,
      idempotencyKey,
    }: ImportVariables): Promise<MigrateImportResponse> => {
      const { data } = await sdkImportLegacyTicket({
        body,
        headers: { "Idempotency-Key": idempotencyKey },
        throwOnError: true,
      });
      return data;
    },
    retry: false,
    onSuccess: (result) => {
      logger.debug("[migrate] import", result.status, result.migration?.ticket_number);
      void queryClient.invalidateQueries({ queryKey: migrateKeys.history() });
    },
  });
}

/** This migrator's last 50 imports. */
export function useMigrationHistory() {
  return useQuery({
    queryKey: migrateKeys.history(),
    queryFn: async (): Promise<MigrationHistoryResponse> => {
      const { data } = await sdkListMigrationHistory({ throwOnError: true });
      return data;
    },
  });
}
